import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Row,
    Col,
    Card,
    Table,
    notification,
    Modal,
    Button,
    Image,
    Pagination,
    Input,
    Tooltip,
    Carousel,
    message,
    Select
} from "antd";
import apiClient from "../../services/api";
import { useLocation } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { baseUrl } from "../../config";
import { skeletonData } from "../../components/skeletons/Blog";
import moment from "moment";
import { handleApiError, handleApiSuccess, handleApiInfo } from "../../utils/errorHandler";
import { createSafeHTML } from "../../utils/sanitize";
import logger from "../../utils/logger";

const { Search } = Input;
const { confirm } = Modal;

function BlogList() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get('user');
    const [userList, setUserList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [privacy, setPrivacy] = useState('');
    const [titleName, setTitleName] = useState();
    const [imageModalVisible, setImageModalVisible] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const [imageType, setImageType] = useState('');

    const columns = [
        {
            title: "#",
            dataIndex: "index",
            key: "index"
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title"
        },
        {
            title: "Slug",
            dataIndex: "slug",
            key: "slug"
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status"
        },
        // {
        //     title: "Type",
        //     dataIndex: "type",
        //     key: "type"
        // },
        {
            title: "Banner Image",
            dataIndex: "bannerImage",
            key: "bannerImage"
        },
        {
            title: "Content",
            dataIndex: "content",
            key: "content"
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category"
        },
        {
            title: "Tags",
            dataIndex: "tags",
            key: "content"
        },
        {
            title: "Created By",
            dataIndex: "createdBy",
            key: "createdBy"
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt"
        },
        {
            title: "Publish At",
            dataIndex: "publishAt",
            key: "publishAt"
        },
        {
            title: 'Action',
            dataIndex: 'enabled',
            fixed: 'right',
            key: 'enabled',
            render: (value, record) => {
                const isActive = record.enabled === true;

                return (
                    <Select
                        value={isActive ? "true" : "false"}
                        className={isActive ? "status-color-active" : "status-color-inactive"}
                        style={{
                            width: 120
                        }}
                        onChange={(newStatus) => handleStatusChange(record._id, newStatus)}
                    >
                        <Select.Option value="true">Active</Select.Option>
                        <Select.Option value="false">Inactive</Select.Option>
                    </Select>
                );
            }
        }
    ];

    useEffect(() => {
        getList()
    }, [searchTerm, currentPage, pageSize, userId]) // eslint-disable-line react-hooks/exhaustive-deps

    async function getList() {
        try {
            setLoading(true);

            const params = {
                page: currentPage,
                items: pageSize,
            };

            if (userId) {
                params.createdBy = userId;
            }

            // Check if search is not null or empty, then add it to params
            if (searchTerm && searchTerm.trim() !== '') {
                params.q = searchTerm;
                params.fields = 'title,createdBy';
                // params.fields = 'name,email'; // Specify searchable fields
            }

            const response = await apiClient.get('/api/blog-admin', { params });
            if (response.data.success) {
                setUserList(response.data.result);
                setTotalUsers(response.data.pagination.count);
            } else {
                setUserList([])
            }
        } catch (error) {
            handleApiError(error);
            logger.error("API error:", error);
            setUserList([]);
        } finally {
            setLoading(false);
        }
    };

    async function handleDelete(id) {
        try {
            const response = await apiClient.delete(`/api/blog/${id}`);
            if (response.status === 200) {
                getList();
                handleApiSuccess('Blog deleted successfully!');
            } else {
                handleApiInfo(response.data.message);
            }
        } catch (error) {
            handleApiError(error);
            logger.error("API error:", error);
        }
    };

    const showModal = (privacy, name) => {
        setTitleName(name);
        setPrivacy(privacy || '')
        setModalVisible(true);
    };

    const handleOk = () => {
        setModalVisible(false);
    };

    const handleCancel = () => {
        setModalVisible(false);
    };

    const handlePageChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleSearch = (value) => {
        if (value.length >= 3 || value.length === 0) {
            setSearchTerm(value);
            setCurrentPage(1);
        }
    };

    const showImageModal = (images, type) => {
        setSelectedImages(images);
        setImageType(type);
        setImageModalVisible(true);
    };

    const handleStatusChange = async (ideaId, newStatus) => {
        try {
            const statusLatest = newStatus === "true" ? true : false;
            const response = await apiClient.put(
                `/api/blog/change-status/${ideaId}`,
                { status: statusLatest }
            );

            if (response.status === 200) {
                message.success('Status updated successfully');
                setUserList(prev =>
                    prev.map(user =>
                        user._id === ideaId ? { ...user, enabled: statusLatest } : user
                    )
                );
            }
        } catch (error) {
            logger.error("Status update error:", error);
            setUserList(prev =>
                prev.map(user =>
                    user._id === ideaId ? { ...user, enabled: user.enabled } : user
                )
            );
            message.error('Failed to update status');
        }
    };

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title="Blog Management"
                            extra={
                                <>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between" }}>
                                        <Search
                                            placeholder="Search by title, created by"
                                            onSearch={handleSearch}
                                            allowClear
                                            style={{ flex: 1, minWidth: "400px" }} // Makes search bar flexible
                                        />
                                        {/* <Link className="custom-btn" to="/blog-management/add">Add</Link> */}
                                    </div>
                                </>
                            }
                        >
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={loading ? skeletonData : userList.map((user, index) => ({
                                        index: (currentPage - 1) * pageSize + index + 1,
                                        _id: user._id,
                                        title: (<Tooltip title={user.title}>
                                            <span>
                                                {user.title?.split(' ').slice(0, 3).join(' ')}
                                                {user.title?.split(' ').length > 3 ? '...' : ''}
                                            </span>
                                        </Tooltip>),
                                        // bannerImage: user.bannerImage ? <Image height="60px" width="60px" src={baseUrl + '/' + user.bannerImage} /> : '-',
                                        bannerImage: Array.isArray(user.bannerImage) && user.bannerImage.length > 0 ? (
                                            <div onClick={() => showImageModal(user.bannerImage, 'Images')} style={{ cursor: 'pointer' }}>
                                                <Carousel autoplay dots={false} style={{ width: 80 }}>
                                                    {user.bannerImage.map((file, i) => {
                                                        const fileUrl = `${file}`;
                                                        const isVideo = /\.(mp4|webm|ogg|MOV)$/i.test(file);

                                                        return (
                                                            <div key={i}>
                                                                {isVideo ? (
                                                                    <video
                                                                        width={60}
                                                                        height={60}
                                                                        style={{ objectFit: 'cover' }}
                                                                        muted
                                                                        loop
                                                                        autoPlay
                                                                    >
                                                                        <source src={fileUrl} type="video/mp4" />
                                                                    </video>
                                                                ) : (
                                                                    <Image
                                                                        src={fileUrl}
                                                                        width={60}
                                                                        height={60}
                                                                        style={{ objectFit: 'cover' }}
                                                                        preview={false}
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </Carousel>
                                            </div>
                                        ) : '-',
                                        slug: (<Tooltip title={user.slug}>
                                            <span>
                                                {user.slug?.split('-').slice(0, 3).join('-')}
                                                {user.slug?.split('-').length > 3 ? '...' : ''}
                                            </span>
                                        </Tooltip>),
                                        // type: user.type,
                                        content: (
                                            <Button type="primary" onClick={() => showModal(user.content, 'Content')}>
                                                <EyeOutlined />
                                            </Button>
                                        ),
                                        tags: user?.tags,
                                        category: user?.category?.title,
                                        status: (
                                            <span className={user?.publish_status === 'publish' ? 'text-success' : 'text-warning'}>
                                                {user?.publish_status === 'publish' ? 'Publish' : 'Draft'}
                                            </span>
                                        ),
                                        createdBy: (
                                            <Link to={`/users/${user?.createdBy?._id}`}>{user?.createdBy?.firstName || ''} {user?.createdBy?.lastName || ''}</Link>
                                        ),
                                        createdAt: (
                                            <p>{moment(user.createdAt).format("MMMM DD, YYYY hh:mm A")}</p>
                                        ),
                                        publishAt: (
                                            user?.publish_status === 'publish' ?
                                                <p>{moment(user.scheduling_date ? user.scheduling_date : user.createdAt).format("MMMM DD, YYYY hh:mm A")}</p> : '-'
                                        ),
                                        enabled: user.enabled
                                    }))}
                                    pagination={false}
                                    scroll={{ x: 'max-content' }}
                                    className="ant-border-space"
                                />
                            </div>
                            {/* Keep pagination outside the scrollable div */}
                            <div className="pagination-container">
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={totalUsers}
                                    onChange={handlePageChange}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
            <Modal
                title={`${titleName}`}
                visible={modalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
            >
                <div className="author-info">
                    <div dangerouslySetInnerHTML={{ __html: createSafeHTML(privacy) }} />
                </div>
            </Modal>
            <Modal
                title={imageType}
                visible={imageModalVisible}
                onCancel={() => setImageModalVisible(false)}
                footer={null}
                width={600}
            >
                <Carousel autoplay>
                    {selectedImages.map((file, i) => {
                        const fileUrl = file?.startsWith('http') ? file : `${baseUrl}/${file}`;
                        const isVideo = /\.(mp4|webm|ogg|MOV)$/i.test(file); // check file extension

                        return (
                            <div key={i} style={{ textAlign: 'center' }}>
                                {isVideo ? (
                                    <video
                                        controls
                                        style={{ maxHeight: '400px', maxWidth: '100%', objectFit: 'contain' }}
                                    >
                                        <source src={fileUrl} type="video/mp4" />
                                    </video>
                                ) : (
                                    <Image
                                        src={fileUrl}
                                        alt={`media-${i}`}
                                        style={{ maxHeight: '400px', maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </Carousel>
            </Modal>
        </>
    );
}

export default BlogList;
